import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { SaasTenantPicker } from './SaasTenantPicker.js';

interface BrandingRow {
  readonly brandingId: string;
  readonly saasTenantId: string;
  readonly applicationName: string;
  readonly primaryColor: string | null;
  readonly status: string;
}

const STATUSES = ['DRAFT', 'PUBLISHED', 'ARCHIVED'];
const PAGE = 25;

/**
 * M43 §27 Branding Management Console (§9 White-Label Management). Operators
 * configure a tenant's white-label profile (application name, logo/favicon
 * handles, theme colours), preview it, and publish (single active profile).
 * Viewing needs BRANDING_VIEW; configure/publish need BRANDING_MANAGE.
 */
export function BrandingManagementConsole(): ReactNode {
  const { token } = useAuth();
  const [rows, setRows] = useState<BrandingRow[]>([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [saasTenantId, setSaasTenantId] = useState('');
  const [applicationName, setApplicationName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#0057b8');
  const [logoRef, setLogoRef] = useState('');
  const [preview, setPreview] = useState<BrandingRow | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: BrandingRow[]; total: number }>(`/v1/saas/branding?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, statusFilter, page, reload]);

  const configure = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!saasTenantId.trim()) { setActionError('A tenant id is required.'); return; }
    if (!applicationName.trim()) { setActionError('An application name is required.'); return; }
    try {
      await apiRequest('/v1/saas/branding', {
        method: 'POST',
        body: { saasTenantId: saasTenantId.trim(), applicationName: applicationName.trim(), primaryColor, logoRef: logoRef.trim() || null },
        token,
      });
      setNotice('Branding configured.');
      setApplicationName('');
      setLogoRef('');
      setPreview(null);
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setActionError('You are not authorised to configure branding (BRANDING_MANAGE required).');
      else setActionError((err as Error).message);
    }
  };

  const showPreview = (): void => {
    setPreview({ brandingId: 'preview', saasTenantId: saasTenantId.trim(), applicationName: applicationName.trim() || '(unnamed)', primaryColor, status: 'DRAFT' });
  };

  const publish = async (id: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/saas/branding/${id}/publish`, { method: 'POST', body: {}, token });
      setNotice('Branding published.');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setActionError('You are not authorised to publish branding (BRANDING_MANAGE required).');
      else setActionError((err as Error).message);
    }
  };

  return (
    <main aria-label="branding-management-console">
      <h1>Branding Management Console</h1>

      <section aria-label="branding-configure">
        <h2>Configure Branding</h2>
        <form aria-label="branding-form" onSubmit={(e) => void configure(e)}>
          <SaasTenantPicker label="Tenant" value={saasTenantId} onPick={setSaasTenantId} />
          <label>Application name<input aria-label="field-app-name" value={applicationName} onChange={(e) => setApplicationName(e.target.value)} /></label>
          <label>Primary colour<input aria-label="field-primary-color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} /></label>
          <label>Logo ref<input aria-label="field-logo-ref" value={logoRef} onChange={(e) => setLogoRef(e.target.value)} /></label>
          <button type="button" aria-label="preview-branding" onClick={showPreview}>Preview</button>
          <button type="submit" aria-label="submit-branding">Save</button>
        </form>
        {preview && (
          <div aria-label="branding-preview" style={{ borderColor: preview.primaryColor ?? undefined }}>
            <span aria-label="preview-app-name">{preview.applicationName}</span>
            <span aria-label="preview-color">{preview.primaryColor}</span>
          </div>
        )}
        {actionError && <p role="alert" aria-label="branding-action-error">{actionError}</p>}
        {notice && <p aria-label="branding-notice">{notice}</p>}
      </section>

      <section aria-label="branding-filters">
        <label>Status
          <select aria-label="filter-status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert" aria-label="branding-error">{error}</p>}
      {loading ? <p>Loading…</p> : (
        <>
          <p aria-label="branding-total">{total} profile(s).</p>
          <table aria-label="branding-table">
            <thead><tr><th scope="col">App</th><th scope="col">Colour</th><th scope="col">Status</th><th scope="col">Actions</th></tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={4}>No branding profiles.</td></tr>
              ) : rows.map((b) => (
                <tr key={b.brandingId} data-branding={b.brandingId}>
                  <td aria-label={`branding-app-${b.brandingId}`}>{b.applicationName}</td>
                  <td>{b.primaryColor ?? '—'}</td>
                  <td aria-label={`branding-status-${b.brandingId}`}>{b.status}</td>
                  <td><button type="button" aria-label={`publish-${b.brandingId}`} onClick={() => void publish(b.brandingId)}>Publish</button></td>
                </tr>
              ))}
            </tbody>
          </table>

          <nav aria-label="branding-pagination">
            <button type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</button>
            <span aria-label="page-indicator">Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE))}</span>
            <button type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>Next</button>
          </nav>
        </>
      )}
    </main>
  );
}
